import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  BookOpen,
  Activity,
  Star,
  PieChart,
  BarChart,
  Activity as ActivityIcon
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import api from '../api';

const BulletinDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/note/dashboard-stats/');
      setStats(response.data.data || response.data);
    } catch (err) {
      setError('Erreur lors du chargement des statistiques');
      console.error('Erreur dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
        <div className="mt-4 text-center">
          <p className="text-gray-500 text-sm">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <FileText className="h-8 w-8 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">{error}</h3>
        <button
          onClick={fetchDashboardStats}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Activity className="h-4 w-4 mr-2" />
          Réessayer
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <BookOpen className="h-8 w-8 text-gray-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Aucune donnée disponible</h3>
        <p className="text-gray-600">Les données apparaîtront une fois que des bulletins seront générés.</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Bulletins',
      value: stats.total_bulletins || 0,
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-200',
      description: 'Générés'
    },
    {
      title: 'Validés',
      value: stats.bulletins_valides || 0,
      icon: CheckCircle,
      color: 'from-green-500 to-green-600',
      bgColor: 'from-green-50 to-green-100',
      borderColor: 'border-green-200',
      description: 'Approuvés'
    },
    {
      title: 'En Attente',
      value: stats.bulletins_en_attente || 0,
      icon: Clock,
      color: 'from-amber-500 to-amber-600',
      bgColor: 'from-amber-50 to-amber-100',
      borderColor: 'border-amber-200',
      description: 'Validation'
    },
    {
      title: 'Moyenne',
      value: stats.moyenne_generale ? `${stats.moyenne_generale}/20` : 'N/A',
      icon: TrendingUp,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100',
      borderColor: 'border-purple-200',
      description: 'Générale'
    }
  ];

  // Préparer les données pour les graphiques
  const evolutionData = stats.evolution_mensuelle?.map(item => ({
    mois: item.mois,
    bulletins: item.bulletins,
    moyennes: item.moyennes
  })) || [];

  const mentionsData = stats.mentions_stats ? Object.entries(stats.mentions_stats)
    .filter(([_, value]) => value > 0)
    .map(([mention, value]) => ({
      name: formatMentionName(mention),
      value: value,
      color: getMentionColor(mention)
    })) : [];

  const classesData = stats.repartition_par_classe?.map(item => ({
    classe: item.classe,
    bulletins: item.bulletins,
    moyenne: item.moyenne,
    taux: item.taux_reussite
  })) || [];

  function getMentionColor(mention) {
    const colors = {
      'EXCELLENT': '#10B981',      // Vert
      'TRES_BIEN': '#3B82F6',     // Bleu
      'BIEN': '#8B5CF6',          // Violet
      'ASSEZ_BIEN': '#F59E0B',    // Jaune/Orange
      'PASSABLE': '#EF4444',      // Rouge
      'INSUFFISANT': '#DC2626',   // Rouge foncé
      'AJOURNE': '#6B7280'        // Gris
    };
    return colors[mention] || '#6B7280';
  }

  function formatMentionName(mention) {
    const names = {
      'EXCELLENT': 'Excellent',
      'TRES_BIEN': 'Très Bien',
      'BIEN': 'Bien',
      'ASSEZ_BIEN': 'Assez Bien',
      'PASSABLE': 'Passable',
      'INSUFFISANT': 'Insuffisant',
      'AJOURNE': 'Ajourné'
    };
    return names[mention] || mention.replace('_', ' ');
  }

  return (
    <div className="space-y-6">
      {/* Cartes de statistiques principales - Taille réduite */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`bg-gradient-to-br ${stat.bgColor} rounded-xl border ${stat.borderColor} p-4 hover:shadow-md transition-all duration-200`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color} shadow-sm`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
              </div>
              
              <div className="mb-2">
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
              </div>
              
              <p className="text-xs text-gray-500">{stat.description}</p>
            </div>
          );
        })}
      </div>

      {/* Graphiques principaux */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Évolution des bulletins */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Évolution Mensuelle</h3>
              <p className="text-sm text-gray-600">Bulletins et moyennes</p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart data={evolutionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" stroke="#6b7280" fontSize={11} />
              <YAxis stroke="#6b7280" fontSize={11} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="bulletins" 
                stackId="1" 
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.3}
                name="Bulletins"
              />
              <Line 
                type="monotone" 
                dataKey="moyennes" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981', strokeWidth: 1, r: 3 }}
                name="Moyennes"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Répartition des mentions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Répartition Mentions</h3>
              <p className="text-sm text-gray-600">Par niveau</p>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-green-50 to-green-100 rounded-lg flex items-center justify-center">
              <PieChart className="h-5 w-5 text-green-600" />
            </div>
          </div>
          
          <ResponsiveContainer width="100%" height={250}>
            <RechartsPieChart>
              <Pie
                data={mentionsData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
              >
                {mentionsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="center"
                wrapperStyle={{
                  paddingTop: '10px',
                  fontSize: '11px'
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Graphique des performances par classe */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Performances par Classe</h3>
            <p className="text-sm text-gray-600">Bulletins et taux de réussite</p>
          </div>
          <div className="w-10 h-10 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg flex items-center justify-center">
            <BarChart className="h-5 w-5 text-purple-600" />
          </div>
        </div>
        
        <ResponsiveContainer width="100%" height={300}>
          <RechartsBarChart data={classesData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="classe" stroke="#6b7280" fontSize={11} />
            <YAxis yAxisId="left" stroke="#3b82f6" fontSize={11} />
            <YAxis yAxisId="right" orientation="right" stroke="#10b981" fontSize={11} />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            />
            <Legend />
            <Bar 
              yAxisId="left" 
              dataKey="bulletins" 
              fill="#3b82f6" 
              radius={[3, 3, 0, 0]}
              name="Bulletins"
            />
            <Bar 
              yAxisId="right" 
              dataKey="taux" 
              fill="#10b981" 
              radius={[3, 3, 0, 0]}
              name="Taux réussite %"
            />
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>

      {/* Top des meilleures moyennes */}
      {stats.top_moyennes && stats.top_moyennes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Top 5 Meilleures Moyennes</h3>
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg flex items-center justify-center">
              <Star className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
          
          <div className="space-y-3">
            {stats.top_moyennes.map((etudiant, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{etudiant.etudiant}</p>
                    <p className="text-sm text-gray-600">{etudiant.classe}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{etudiant.moyenne}/20</p>
                  <p className="text-xs text-gray-500">{formatMentionName(etudiant.mention)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulletinDashboard; 